/**
 * @file src/dashboard.js
 * Bảng nạp bài: ném một file `.glb` và một file chữ `.json` vào, bấm dựng là ra bài chạy
 * ngay trong trang này.
 *
 * Không có bước build, không có server nào ở giữa: model được đọc bằng chính luật ở
 * `src/core/lessonData.js` — CÙNG một luật mà `tools/gen-binding.mjs` dùng lúc build,
 * nên bài xem thử ở đây và bài build ra là một.
 *
 * SỐ BƯỚC LUÔN THEO MODEL. File chữ thiếu bước nào thì bước đó vẫn còn đủ trong bài, chỉ
 * là không có tên và không có chữ. Thà hiện một bước trống còn hơn nuốt mất nó: mất bước
 * là mất cả góc nhìn lẫn lớp cắt mà không ai nhận ra.
 */

import './styles/base.css';
import './dashboard.css';
import { bootLesson, LOADING_BG_KEY } from './core/boot.js';
import {
  đọcGlb,
  quétModel,
  gomChữ,
  rótChữ,
  thùngRỗng,
  dựngBảnGộp,
  có,
  ôHoặcChuỗi,
  SCENE_PREFIXES,
  VIEW_MẶC_ĐỊNH,
  GLASS_MẶC_ĐỊNH,
} from './core/lessonData.js';
import { T } from './dashboard.i18n.js';

/** Tiết đóng vai KHUNG CHẠY. Mọi thứ hiển thị đều đến từ file người dùng nạp vào. */
const KHUNG = () => import('./lessons/his-06-0001-ai-cap/lesson.js');
/** Từ điển của TIẾT (boot.js tự trộn thêm chuỗi của shell). Khoá của object này cũng là
 * danh sách ngôn ngữ mà nút đổi ngôn ngữ hiện ra. */
const TỪ_ĐIỂN = {
  vi: () => import('./lessons/his-06-0001-ai-cap/i18n/vi.json'),
  en: () => import('./lessons/his-06-0001-ai-cap/i18n/en.json'),
};
/** Bỏ khoá bắt đầu bằng `_` — đó là ghi chú trong file i18n, không phải chuỗi hiển thị. */
const bỏGhiChú = (/** @type {Record<string, string>} */ d) =>
  Object.fromEntries(Object.entries(d).filter(([k]) => !k.startsWith('_')));

const el = (
  /** @type {string} */ tag,
  /** @type {any} */ attrs = {},
  /** @type {any[]} */ con = [],
) => {
  const n = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'class') n.className = String(v);
    else if (k === 'text') n.textContent = String(v);
    else if (k.startsWith('on')) n.addEventListener(k.slice(2), /** @type {any} */ (v));
    else n.setAttribute(k, String(v));
  }
  for (const c of con) n.appendChild(c);
  return n;
};

/** @type {{glb: File|null, json: File|null, poster: File|null}} */
const đãChọn = { glb: null, json: null, poster: null };

const sânKhấu = /** @type {HTMLElement} */ (document.getElementById('app'));
const bảng = /** @type {HTMLElement} */ (document.getElementById('dash'));

function dựngGiaoDiện() {
  const ô = (/** @type {'glb'|'json'|'poster'} */ loại) => {
    const nhãn = el('div', { class: 'dz__label', text: T(`dz.${loại}`) });
    const phụ = el('div', { class: 'dz__hint', text: T(`dz.${loại}.hint`) });
    const tên = el('div', { class: 'dz__file', text: '' });
    const nhậnLoại = { glb: '.glb,.gltf', json: '.json', poster: 'image/*' };
    const inp = el('input', { type: 'file', accept: nhậnLoại[loại] });
    const hộp = el('label', { class: `dz dz--${loại}` }, [nhãn, phụ, tên, inp]);
    const nhận = (/** @type {File|null} */ f) => {
      if (!f) return;
      đãChọn[loại] = f;
      tên.textContent = `${f.name} · ${(f.size / 1048576).toFixed(1)} MB`;
      hộp.classList.add('is-set');
      cậpNhậtNút();
    };
    inp.addEventListener('change', () => nhận(/** @type {any} */ (inp).files?.[0] ?? null));
    for (const ev of ['dragenter', 'dragover'])
      hộp.addEventListener(ev, (e) => {
        e.preventDefault();
        hộp.classList.add('is-over');
      });
    for (const ev of ['dragleave', 'drop'])
      hộp.addEventListener(ev, () => hộp.classList.remove('is-over'));
    hộp.addEventListener('drop', (e) => {
      e.preventDefault();
      nhận(/** @type {any} */ (e).dataTransfer?.files?.[0] ?? null);
    });
    return hộp;
  };

  const nút = el('button', { class: 'dash__go', type: 'button', disabled: 'true' });
  nút.textContent = T('go');
  nút.addEventListener('click', () => dựngBài(nút));
  const báo = el('div', { class: 'dash__report', id: 'report' });

  bảng.appendChild(
    el('div', { class: 'dash' }, [
      el('h1', { class: 'dash__title', text: T('title') }),
      el('p', { class: 'dash__lead', text: T('lead') }),
      el('div', { class: 'dash__zones', id: 'zones' }, [ô('glb'), ô('json'), ô('poster')]),
      nút,
      báo,
    ]),
  );
  cậpNhậtNút();

  function cậpNhậtNút() {
    /** @type {any} */ (nút).disabled = !đãChọn.glb;
  }
}

/** @param {File} f @returns {Promise<ArrayBuffer>} */
const đọcBuffer = (f) => f.arrayBuffer();
/** @param {File} f @returns {Promise<any>} */
const đọcJson = async (f) => JSON.parse(await f.text());

/**
 * Đối chiếu model với file chữ rồi in ra bảng: bước nào có chữ, bước nào chưa.
 * Đây là phần quan trọng nhất của trang này — nạp xong mà không biết mình thiếu gì thì
 * bài lên vẫn "chạy được", chỉ là trống chữ ở những chỗ không ai để ý.
 * @param {any[]} scenes @param {any} content @param {any} merged
 * @param {string[]} [cảnhBáo] lỗi khai báo bên model — xem quétModel()
 */
function inBáoCáo(scenes, content, merged, cảnhBáo = []) {
  const báo = /** @type {HTMLElement} */ (document.getElementById('report'));
  báo.textContent = '';
  const idsChữ = new Set((content?.scenes ?? []).map((/** @type {any} */ s) => String(s.id)));
  const thiếu = scenes.filter((b) => !idsChữ.has(String(b.id)));

  const hàng = (/** @type {any} */ b, /** @type {any} */ m) => {
    const tên = ôHoặcChuỗi(m.name);
    const sốÔ = 1 + m.texts.length * 2;
    const đãĐiền =
      (có(m.TT) ? 1 : 0) +
      m.texts.reduce(
        (/** @type {number} */ n, /** @type {any} */ t) =>
          n + (có(t.title) ? 1 : 0) + (có(t.desc) ? 1 : 0),
        0,
      );
    return el('tr', { class: tên ? '' : 'row--noname' }, [
      el('td', { text: b.id }),
      el('td', { text: tên ? (tên.vi ?? tên.en ?? '') : T('noname') }),
      el('td', { text: String(m.texts.length) }),
      el('td', { text: `${đãĐiền}/${sốÔ}` }),
      el('td', { text: b.animation ? `${b.animation.clip} (${b.animation.type})` : '—' }),
    ]);
  };

  const bảng = el('table', { class: 'rp__table' }, [
    el('thead', {}, [
      el(
        'tr',
        {},
        ['col.scene', 'col.name', 'col.notes', 'col.filled', 'col.anim'].map((k) =>
          el('th', { text: T(k) }),
        ),
      ),
    ]),
    el(
      'tbody',
      {},
      scenes.map((b, i) => hàng(b, merged.scenes[i])),
    ),
  ]);

  const tóm = el('p', { class: 'rp__sum' });
  tóm.textContent = T('sum')
    .replace('{model}', String(scenes.length))
    .replace('{json}', String(idsChữ.size));
  báo.appendChild(tóm);
  if (đãChọn.poster) {
    const p = el('p', { class: 'rp__sum' });
    p.textContent = T('sum.poster').replace('{ten}', đãChọn.poster.name);
    báo.appendChild(p);
  }
  if (thiếu.length) {
    const c = el('p', { class: 'rp__warn' });
    c.textContent = T('warn.missing')
      .replace('{n}', String(thiếu.length))
      .replace('{ids}', thiếu.map((b) => b.id).join(', '));
    báo.appendChild(c);
  }
  // LỖI KHAI BÁO BÊN MODEL. Đặt TRƯỚC bảng, cùng chỗ với cảnh báo thiếu bước: đây là
  // thứ phải sửa bên Blender, không sửa được bằng cách gõ thêm vào file chữ.
  if (cảnhBáo.length) {
    const khối = el('div', { class: 'rp__warn' });
    khối.appendChild(el('p', { text: T('warn.model').replace('{n}', String(cảnhBáo.length)) }));
    khối.appendChild(
      el(
        'ul',
        { class: 'rp__warnlist' },
        cảnhBáo.map((c) => el('li', { text: c })),
      ),
    );
    báo.appendChild(khối);
  }
  báo.appendChild(bảng);

  // TẢI BẢN GỘP. Merge xong mà không lấy được file thì trang này chỉ để xem cho vui —
  // `lesson.json` mới là thứ đẩy lên CMS, và từ lúc đó mọi chỉnh sửa nằm ở nó.
  const tải = el('button', { class: 'dash__dl', type: 'button' });
  tải.textContent = T('download');
  tải.addEventListener('click', () => {
    const a = el('a', {
      href: URL.createObjectURL(
        new Blob([JSON.stringify(merged, null, 2)], { type: 'application/json' }),
      ),
      download: 'lesson.json',
    });
    a.click();
    setTimeout(() => URL.revokeObjectURL(/** @type {any} */ (a).href), 4000);
  });
  báo.appendChild(tải);
}

/** Nút bật/tắt ngăn kéo, dựng một lần. */
function nútNgănKéo() {
  const b = el('button', { class: 'dash__toggle', type: 'button' });
  b.textContent = T('drawer');
  b.addEventListener('click', () => bảng.classList.toggle('is-open'));
  return b;
}

/** @param {HTMLElement} nút */
async function dựngBài(nút) {
  const báo = /** @type {HTMLElement} */ (document.getElementById('report'));
  /** @type {any} */ (nút).disabled = true;
  nút.textContent = T('working');
  try {
    const glb = /** @type {File} */ (đãChọn.glb);
    const buf = await đọcBuffer(glb);
    const g = đọcGlb(buf);
    const { scenes, chữCảnh, TB, cảnhBáo } = quétModel(g);
    if (!scenes.length) throw new Error(T('err.noscene'));

    const content = đãChọn.json ? await đọcJson(đãChọn.json) : null;
    const thùng = thùngRỗng();
    // Không có bản đồ node của lần trước — file chữ tự mang `id` của bước, còn khoá chú
    // thích thì khớp với khoá bên 3D gán trong extras (`CT1`). Đó là hợp đồng giữa hai bên.
    gomChữ(content, { scenes }, thùng);
    rótChữ(scenes, chữCảnh, thùng);

    // ĐÃ NẠP FILE CHỮ THÌ FILE CHỮ LÀ THẨM QUYỀN. Bước nào file chữ không nhắc tới thì
    // bước đó ĐỂ TRỐNG — kể cả khi model có sẵn extras cho nó. Lấy extras lấp vào nghe
    // thì tử tế, nhưng người nạp sẽ thấy một cái tên hiện ra và tưởng mình đã điền đủ,
    // trong khi file chữ giao cho CMS vẫn thiếu bước đó.
    // Không nạp file chữ nào thì ngược lại: extras là nguồn duy nhất, cứ dùng.
    if (content) {
      const cóTrongChữ = new Set(
        (content.scenes ?? []).map((/** @type {any} */ x) => String(x.id)),
      );
      for (const b of scenes) {
        if (cóTrongChữ.has(String(b.id))) continue;
        b.name = null;
        const ô = chữCảnh.get(b.id);
        if (ô) for (const k of Object.keys(ô)) ô[k] = { vi: '', en: '' };
      }
    }

    const binding = {
      schemaVersion: 1,
      lessonId: content?.lesson?.id ?? null,
      model: {
        asset: 'model.main',
        file: glb.name,
        root: 'Scene Collection',
        scenePrefixes: SCENE_PREFIXES,
      },
      view: { ...VIEW_MẶC_ĐỊNH },
      annotation: { staggerMs: 60 },
      cutaway: { fadeMs: 1100, startAt: 1, litInside: true },
      glass: { ...GLASS_MẶC_ĐỊNH },
      scenes,
    };
    const lesson = {
      ...(content?.lesson ?? {}),
      TB: có(content?.lesson?.TB) ?? TB ?? { vi: glb.name, en: glb.name },
    };
    const merged = dựngBảnGộp(binding, lesson, chữCảnh);

    inBáoCáo(scenes, content, merged, cảnhBáo);
    await chạy(glb, merged, lesson);
  } catch (err) {
    báo.textContent = '';
    const p = el('p', { class: 'rp__err' });
    p.textContent = `${T('err')} ${String(/** @type {any} */ (err)?.message ?? err)}`;
    báo.appendChild(p);
    console.error('[dashboard]', err);
    /** @type {any} */ (nút).disabled = false;
    nút.textContent = T('go');
  }
}

/**
 * Nạp bài bằng chính đường của bản chạy thật: một manifest, một bản gộp.
 * Model và JSON được cấp qua `blob:` URL nên AssetLoader không cần biết chúng đến từ đâu
 * — không phải thêm nhánh "đọc từ bộ nhớ" vào lõi.
 * @param {File} glb @param {any} merged @param {any} lesson
 */
async function chạy(glb, merged, lesson) {
  const urlGlb = URL.createObjectURL(glb);
  // POSTER đi vào HAI chỗ khác nhau, đừng nhầm:
  //   img.loadingBg → nền màn hình TẢI (boot.js đọc theo khoá quy ước này)
  //   info.poster   → ảnh dự phòng khi máy không chạy được 3D (ErrorScreen)
  // Chỉ cấp một trong hai thì hoặc màn tải trống trơn, hoặc máy yếu mở ra thấy khung rỗng.
  const urlPoster = đãChọn.poster ? URL.createObjectURL(đãChọn.poster) : null;
  const urlJson = URL.createObjectURL(
    new Blob([JSON.stringify(merged)], { type: 'application/json' }),
  );

  const mod = (await KHUNG()).default;
  /** @type {Record<string, Record<string, string>>} */
  const dicts = {};
  for (const [l, imp] of Object.entries(TỪ_ĐIỂN)) dicts[l] = bỏGhiChú((await imp()).default);
  /** @type {any} */
  const manifest = {
    scope: 'lesson',
    scopeId: 'dashboard',
    assetRoot: '',
    assets: {
      // `shared: true` để AssetLoader KHÔNG ghép thêm assetRoot vào trước — blob: là URL
      // tuyệt đối, ghép thêm là hỏng đường dẫn.
      'model.main': { type: 'gltf', path: urlGlb, critical: true, shared: true, bytes: glb.size },
      'lesson.merged': { type: 'json', path: urlJson, critical: false, shared: true },
      ...(urlPoster
        ? { [LOADING_BG_KEY]: { type: 'texture', path: urlPoster, critical: false, shared: true } }
        : {}),
    },
  };

  // BẢNG NẠP KHÔNG BỊ XOÁ. Nó thu về một ngăn kéo bên phải, bật/tắt bằng nút góc trái —
  // cái bảng đối chiếu bước ↔ chữ là thứ người ta cần xem LẠI sau khi thấy bài chạy, xoá
  // đi là mất luôn chỗ duy nhất nói bước nào chưa có chữ.
  document.body.classList.add('is-running');
  sânKhấu.hidden = false;
  bảng.appendChild(nútNgănKéo());
  await bootLesson({
    root: sânKhấu,
    lesson: mod,
    manifest,
    dicts,
    info: {
      title: có(lesson.TB) ?? { vi: glb.name, en: glb.name },
      grade: lesson.grade ?? null,
      subjectName: lesson.subjectName ?? null,
      ...(urlPoster ? { poster: urlPoster } : {}),
    },
    buildVersion: 0,
    baseUrl: '/',
  });
}

dựngGiaoDiện();
